import { useEffect, useState, type RefObject } from "react";

type Options = {
  /** The smallest an item may be, in rem, before one fewer is shown instead. */
  minWidthRem: number;
  minHeightRem: number;
  gapRem: number;
  maxColumns: number;
};

export type FitCount = { columns: number; rows: number };

/**
 * How many items of a minimum size fit in a box, as columns and rows. The site never
 * scrolls, so a list shows exactly what its space holds and pages through the rest; this is
 * what decides "exactly what its space holds", and it answers again whenever the box changes.
 */
export function useFitCount(box: RefObject<HTMLElement | null>, options: Options): FitCount {
  const { minWidthRem, minHeightRem, gapRem, maxColumns } = options;
  const [fit, setFit] = useState<FitCount>({ columns: 1, rows: 1 });

  useEffect(() => {
    const element = box.current;
    if (!element) return;
    const measure = () => {
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const gap = gapRem * rem;
      const across = Math.floor((element.clientWidth + gap) / (minWidthRem * rem + gap));
      const down = Math.floor((element.clientHeight + gap) / (minHeightRem * rem + gap));
      const next = { columns: Math.min(Math.max(across, 1), maxColumns), rows: Math.max(down, 1) };
      setFit((current) =>
        current.columns === next.columns && current.rows === next.rows ? current : next,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [box, minWidthRem, minHeightRem, gapRem, maxColumns]);

  return fit;
}
